/**
 * Per-ECU detection + region tables for direct (DS2) flashing.
 *
 * The IDENT response from a DS2 ECU includes ASCII identification bytes;
 * `identifyEcu()` heuristically matches against known signatures and
 * returns the EcuVariant + the writable region tables for both FULL and
 * CALIBRATION modes.
 *
 * Region tables describe the addresses tools actually push through the
 * DS2 wire (a subset of the broader ECU memory map). Going outside
 * them per the protocol is possible (§5/§7) but no DS2 tool exercises
 * it; for bench-only out-of-range writes use the bootmode path in
 * `@emdzej/nfsx-bootmode`.
 */
/**
 * MS42 — Siemens, BMW M52TU, Infineon C167CR_SR.
 *
 * WRITE regions (mirrors upstream tooling's — never write the
 * bootloader, and stop 16-44 bytes short of each erase block boundary
 * to spare reserved flash cells):
 *   0x11000-0x3FFFF — lower program (192 KB)
 *   0x5002C-0x7FFFF — upper program (~192 KB; skips 0x2C-byte header)
 *   0x48000-0x4FFEF — calibration (32 KB - 16; skips checksum trailer)
 *
 * READ regions (mirrors upstream tooling's dump output, broader
 * than the writeable range):
 *   0x00000-0x0BFFF — C167 internal flash (bootloader, read-only)
 *   0x11000-0x3FFFF — lower program
 *   0x48000-0x4FFFF — calibration (includes the 16-byte trailer)
 *   0x50000-0x7FFFF — upper program (includes the 0x2C-byte header)
 */
const MS42_PROFILE = {
    variant: 'MS42',
    ds2Addr: 0x12,
    blockSize: 118,
    readBlockSize: 123,
    binSize: 0x80000,
    fullRegions: [
        // Both program regions share ONE erase at 0x11000 — empirically
        // that single 0x07 0x06 command erases everything between the
        // bootloader and the cal block. Doing a second erase between them
        // would wipe the data we just wrote.
        { start: 0x11000, end: 0x3ffff, binOffset: 0x11000, eraseAddr: 0x11000 },
        { start: 0x5002c, end: 0x7ffff, binOffset: 0x5002c, eraseAddr: 0x11000 },
        { start: 0x48000, end: 0x4ffef, binOffset: 0x48000, eraseAddr: 0x48000 },
    ],
    readFullRegions: [
        { start: 0x00000, end: 0x0bfff, binOffset: 0x00000 }, // C167 internal flash
        { start: 0x11000, end: 0x3ffff, binOffset: 0x11000 },
        { start: 0x48000, end: 0x4ffff, binOffset: 0x48000 }, // includes trailer
        { start: 0x50000, end: 0x7ffff, binOffset: 0x50000 }, // includes header
    ],
    calibrationRegions: [
        // WRITE: 0x48000-0x4FFEF (32 KB - 16). Last 16 bytes are the
        // Calibration CRC trailer (0x4FFF0-0x4FFFF) — read-only.
        { start: 0x48000, end: 0x4ffef, binOffset: 0x48000 },
    ],
    readCalibrationRegions: [
        // READ: full 32 KB block 0x48000-0x4FFFF (includes trailer).
        { start: 0x48000, end: 0x4ffff, binOffset: 0x48000 },
    ],
    identKey: '111011',
    identSignatures: ['1430844', '7503355'],
    calibrationVerified: true,
};
/**
 * MS43 — Siemens, BMW M54, Infineon C167CS-32F.
 *
 * FULL layout (512 KB BIN, ECU-side addresses):
 *
 *   ECU 0x00000-0x0BFFF → BIN 0x00000  C167 internal flash (48 KB)
 *   skipped 0x0C000-0x0FFFF              C167 SFR / RAM window
 *   ECU 0x90000-0xEFFFF → BIN 0x10000  external program (384 KB, ECU
 *                                       applies 0x9 → 0x1 high-byte
 *                                       translation to chip address)
 *   ECU 0x70000-0x7FFFF → BIN 0x70000  external calibration (64 KB)
 *
 * CALIBRATION: only the 64 KB block at ECU 0x70000-0x7FFFF.
 */
const MS43_PROFILE = {
    variant: 'MS43',
    ds2Addr: 0x12,
    blockSize: 118,
    readBlockSize: 123,
    binSize: 0x80000,
    fullRegions: [
        // Single erase at 0x90000 covers the whole program range;
        // single erase at 0x70000 covers the cal range. Stops 16-17 bytes
        // short of each region end to spare the read-only trailers.
        { start: 0x90000, end: 0xeffef, binOffset: 0x10000 }, // program, 393200
        { start: 0x70000, end: 0x7ffee, binOffset: 0x70000 }, // calibration, 65519
    ],
    readFullRegions: [
        // READ: full ranges including bootloader + checksum trailers.
        { start: 0x00000, end: 0x0bfff, binOffset: 0x00000 }, // bootloader
        { start: 0x90000, end: 0xeffff, binOffset: 0x10000 }, // full 393216
        { start: 0x70000, end: 0x7ffff, binOffset: 0x70000 }, // full 65536
    ],
    calibrationRegions: [
        // WRITE: 0x70000-0x7FFEE (65519 bytes). Last 17 bytes are the
        // calibration trailer (0x7FFEF-0x7FFFF), read-only.
        { start: 0x70000, end: 0x7ffee, binOffset: 0x70000 },
    ],
    readCalibrationRegions: [
        // READ: full 64 KB block including trailer.
        { start: 0x70000, end: 0x7ffff, binOffset: 0x70000 },
    ],
    identKey: '111430',
    identSignatures: ['1430866', '7516126', '7508552', '7545150'],
    calibrationVerified: true,
};
/**
 * GS20 — Siemens transmission control unit (TCU).
 *
 * BIN format: **512 KB (0x80000)** — same total size as the MS-class
 * engine BIN. Of that, only two regions are written; bytes
 * `0x00000-0x0FFFF` and `0x60000-0x7FFFF` are header / padding.
 *
 *   BIN `0x10000-0x1FFFF` → ECU `0x90000-0x9FFFF`  (64 KB program)
 *   BIN `0x20000-0x5FFFF` → ECU `0xA0000-0xDFFFF`  (256 KB data)
 *
 * CALIBRATION (partial-write) — rewrites only the 64 KB program block
 * at ECU `0x90000-0x9FFFF`, sourced from BIN `0x10000-0x1FFFF` when a
 * full 512 KB BIN is supplied.
 */
const GS20_PROFILE = {
    variant: 'GS20',
    ds2Addr: 0x32,
    // GS20 (key "G2210_") maps to upstream tooling's `r` protocol class which
    // declares (251 read, 246 write) — high-capacity TCU variant.
    blockSize: 246,
    readBlockSize: 251,
    binSize: 0x80000,
    fullRegions: [
        // erase 0xA0000, write ECU[0xA0000..0xDFFFF] from BIN[0x20000..0x5FFFF]
        { start: 0xa0000, end: 0xdffff, binOffset: 0x20000 },
        // erase 0x90000, write ECU[0x90000..0x9FFFF] from BIN[0x10000..0x1FFFF]
        { start: 0x90000, end: 0x9ffff, binOffset: 0x10000 },
    ],
    calibrationRegions: [
        // partial-write covers ONLY ECU[0x90000..0x9FFFF]
        { start: 0x90000, end: 0x9ffff, binOffset: 0x10000 },
    ],
    identKey: 'G2210_',
    identSignatures: ['1422778', '1422779'],
    calibrationVerified: true,
};
export const ALL_PROFILES = [
    MS42_PROFILE,
    MS43_PROFILE,
    GS20_PROFILE,
];
/**
 * Try to identify the ECU from the bytes of an IDENT response payload
 * (the bytes after STATUS in the response, not including XOR).
 *
 * Returns the matched profile, or null if none of the signatures fired.
 */
export function identifyEcu(identPayload) {
    let ascii = '';
    for (let i = 0; i < identPayload.length; i++)
        ascii += String.fromCharCode(identPayload[i]);
    for (const profile of ALL_PROFILES) {
        for (const sig of profile.identSignatures) {
            if (ascii.includes(sig))
                return profile;
        }
    }
    return null;
}
export function getProfile(variant) {
    const p = ALL_PROFILES.find((x) => x.variant === variant);
    if (!p)
        throw new Error(`unknown ECU variant: ${variant}`);
    return p;
}
/**
 * Pick the right region table for the requested mode + purpose. Read
 * tables include bootloader + checksum trailers (mirrors upstream tooling's
 * dump output); write tables exclude them (mirrors upstream tooling's safe
 * write path — writing the bootloader or trailer bricks the ECU).
 * Falls back to the write table when no read table is defined.
 */
/**
 * Look up an ECU profile by its 6-character dispatch key, returned by
 * the hardware-reference identification sequence (cmd 0x0D → cmd 0x06).
 */
export function findByIdentKey(key) {
    for (const p of ALL_PROFILES) {
        if (p.identKey === key)
            return p;
    }
    return null;
}
export function pickRegions(profile, mode, purpose = 'write') {
    if (purpose === 'read') {
        if (mode === 'full') {
            return profile.readFullRegions ?? profile.fullRegions;
        }
        return profile.readCalibrationRegions ?? profile.calibrationRegions;
    }
    return mode === 'full' ? profile.fullRegions : profile.calibrationRegions;
}
/**
 * Total bytes the host will push to the wire for a given mode + purpose,
 * ignoring 0xFF skipping. Useful for progress accounting.
 */
export function totalBytesForMode(profile, mode, purpose = 'write') {
    let n = 0;
    for (const r of pickRegions(profile, mode, purpose))
        n += r.end - r.start + 1;
    return n;
}
//# sourceMappingURL=ecu-tables.js.map